import React, { useEffect } from "react";

// Design tokens (match your pink theme)
const PINK = "#d86a78";
const PINK_SOFT = "#f2c6cc";

/**
 * ConfirmModal (design-matched)
 * - Background blur + dim
 * - Center card with top-right X
 * - Optional: YES/NO buttons (confirm)
 * - Optional: info-only modal (no buttons)
 */
export default function ConfirmModal({
  open,
  variant = "confirm", // "confirm" | "info"
  icon = "ban", // "ban" | "trash" | "x" | "success"
  title,
  description,
  helper,
  confirmText = "YES",
  cancelText = "NO",
  onConfirm,
  onClose,
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[9999]">
      {/* Backdrop (blur like design) */}
      <div
        className="absolute inset-0"
        style={{
          background: "rgba(255,255,255,0.30)",
          backdropFilter: "blur(3px)",
        }}
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative z-[1] flex min-h-full items-center justify-center p-4">
        <div
          className="relative w-[620px] max-w-[92vw] rounded-[16px] bg-white px-8 py-10"
          style={{
            border: `1px solid ${PINK_SOFT}`,
            boxShadow: "0 24px 70px rgba(0,0,0,0.14)",
          }}
        >
          {/* close */}
          <button
            type="button"
            onClick={onClose}
            className="absolute right-6 top-5 text-[18px] font-semibold"
            style={{ color: PINK }}
            aria-label="Close"
          >
            x
          </button>

          {/* icon */}
          <div className="mx-auto mb-6 flex h-[120px] w-[120px] items-center justify-center">
            {icon === "ban" ? <BanIcon /> : null}
            {icon === "trash" ? <TrashIcon /> : null}
            {icon === "x" ? <BigXIcon /> : null}
            {icon === "success" ? <SuccessIcon /> : null}
          </div>

          {/* title + body */}
          <div className="mx-auto max-w-[520px] text-center">
            <h3 className="text-[18px] font-semibold text-slate-700">{title}</h3>
            {description ? (
              <p className="mt-4 text-[13px] leading-6 text-slate-500">{description}</p>
            ) : null}
            {helper ? <p className="mt-3 text-[12px] text-slate-500">{helper}</p> : null}
          </div>

          {variant === "confirm" ? (
            <div className="mt-9 flex items-center justify-center gap-5">
              <button
                type="button"
                onClick={onClose}
                className="h-[34px] w-[140px] rounded-[6px] text-[12px] font-semibold text-white"
                style={{ background: "#6B6B6B" }}
              >
                {cancelText}
              </button>

              <button
                type="button"
                onClick={onConfirm}
                className="h-[34px] w-[140px] rounded-[6px] text-[12px] font-semibold text-white"
                style={{ background: PINK }}
              >
                {confirmText}
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function BanIcon() {
  return (
    <svg width="120" height="120" viewBox="0 0 120 120" fill="none">
      <circle cx="60" cy="60" r="46" stroke={PINK} strokeWidth="12" />
      <line
        x1="32"
        y1="32"
        x2="88"
        y2="88"
        stroke={PINK}
        strokeWidth="12"
        strokeLinecap="round"
      />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg width="120" height="120" viewBox="0 0 120 120" fill="none">
      <rect x="34" y="44" width="52" height="56" rx="10" fill={PINK} />
      <rect x="30" y="34" width="60" height="14" rx="7" fill={PINK} />
      <rect x="46" y="28" width="28" height="10" rx="5" fill={PINK} />
      <rect x="48" y="56" width="6" height="32" rx="3" fill="white" opacity="0.85" />
      <rect x="59" y="56" width="6" height="32" rx="3" fill="white" opacity="0.85" />
      <rect x="70" y="56" width="6" height="32" rx="3" fill="white" opacity="0.85" />
    </svg>
  );
}

function BigXIcon() {
  return (
    <svg width="120" height="120" viewBox="0 0 120 120" fill="none">
      <line x1="30" y1="30" x2="90" y2="90" stroke="#ff0000" strokeWidth="18" strokeLinecap="round" />
      <line x1="90" y1="30" x2="30" y2="90" stroke="#ff0000" strokeWidth="18" strokeLinecap="round" />
    </svg>
  );
}

function SuccessIcon() {
  return (
    <svg width="120" height="120" viewBox="0 0 120 120" fill="none">
      <circle cx="60" cy="60" r="46" stroke="#d86a75" strokeWidth="10" />
      <path
        d="M38 62L52 76L82 44"
        stroke="#d86a75"
        strokeWidth="12"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
